// @flow

import { ChartPreviewRenderer } from './chart-preview.renderer';
import { ChartModel } from './chart.model';
import { ChartRenderer } from './chart.renderer';
import { CANVAS_HEIGHT, CANVAS_WIDTH, CHART_AREA_RECT, CHART_PREVIEW_REC, SCALE_NUMBER, SCALE_RECT } from './constants';
import { MarkRenderer } from './mark.renderer';
import { ScaleRenderer } from './scale.renderer';
import { getChartMaxValue } from './utils/chart.utils';

export class CanvasRenderer {
    canvas: HTMLCanvasElement;
    context: CanvasRenderingContext2D;
    chart: ChartModel;
    previewArea: ChartPreviewRenderer;
    chartArea: ChartRenderer;
    scale: ScaleRenderer;
    mark: MarkRenderer;
    constructor (canvas: HTMLCanvasElement, chart: ChartModel) {
        this.canvas = canvas;
        this.context = this.canvas.getContext('2d');
        this.chart = chart;
        this.chartArea = new ChartRenderer(this.chart, CHART_AREA_RECT.x2 - CHART_AREA_RECT.x1, CHART_AREA_RECT.y2 - CHART_AREA_RECT.y1);
        this.previewArea = new ChartPreviewRenderer(this.chart, CHART_PREVIEW_REC.x2 - CHART_PREVIEW_REC.x1, CHART_PREVIEW_REC.y2 - CHART_PREVIEW_REC.y1);
        this.scale = new ScaleRenderer(this.chart, SCALE_RECT.x2 - SCALE_RECT.x1, SCALE_RECT.y2 - SCALE_RECT.y1);
        this.mark = new MarkRenderer(this.chart, CHART_AREA_RECT.x2 - CHART_AREA_RECT.x1, CHART_AREA_RECT.y2 - CHART_AREA_RECT.y1);
        (this: any).changeSelection = this.changeSelection.bind(this);
        this.draw();
        this.canvas.addEventListener('click', (event: MouseEvent) => {
            if (event.offsetX >= CHART_AREA_RECT.x1 &&
                event.offsetX <= CHART_AREA_RECT.x2 &&
                event.offsetY >= CHART_AREA_RECT.y1 &&
                event.offsetY <= CHART_AREA_RECT.y2
            ) {
                let offset = event.offsetX;
                const step = CANVAS_WIDTH / (this.chart.selectEnd - 1 - this.chart.selectStart);
                const chartOffset = Math.round(offset / step) + this.chart.selectStart;
                this.mark.update({ position: chartOffset });
                this.draw();
            }
        }, false);
        window.addEventListener('click', (event: MouseEvent) => {
            this.mark.show = false;
            this.draw();
        }, false);
    }

    toggleColumn (columnKey: string) {
        const column = this.chart.columns.find(c => c.key === columnKey);
        if (column) {
            column._disabled = !column._disabled;
        }
        const max = this.chart.getMaxValue();
        const chartMax = getChartMaxValue(max, SCALE_NUMBER);
        this.previewArea.update({ chartMax: max, toggleColumn: columnKey });
        this.chartArea.update({ chartMax, toggleColumn: columnKey });
        this.scale.update({ chartMax });
        this.mark.update({ chartMax, toggleColumn: columnKey });
        this.draw();
    }

    changeSelection (start: number, end: number) {
        this.previewArea.update({ move: { start, end } });
        this.chartArea.update({ move: { start, end } });
        this.scale.update({ move: { start, end } });
        this.mark.update({ move: { start, end } });
        this.draw();
    }

    draw () {
        const _draw = () => {
            if (this.previewArea.progressAnimation || this.chartArea.progressAnimation || this.scale.progressAnimation || this.mark.progressAnimation) {
                this.clear();
                this.context.drawImage(this.chartArea.draw(), CHART_AREA_RECT.x1, CHART_AREA_RECT.y1);
                this.context.drawImage(this.previewArea.draw(), CHART_PREVIEW_REC.x1, CHART_PREVIEW_REC.y1);
                this.context.drawImage(this.scale.draw(), SCALE_RECT.x1, SCALE_RECT.y1);
                this.context.drawImage(this.mark.draw(), CHART_AREA_RECT.x1, CHART_AREA_RECT.y1);
                window.requestAnimationFrame(_draw);
            }
        };
        _draw();
    }

    clear () {
        this.context.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    }
}
